<template>
	<div v-if="isUserAllowedInLevel5or6 || this.isUserAllowedInLevel1">
		<BaseHeader :title="$t('company_ads').toString()">
			<div v-if="isAdAdmin" class="flex items-center">
				<div @click="clearAndCreate" class="btn-default">
					<font-awesome-icon :icon="['fa', 'plus']" class="w-5 h-5 pr-1" size="lg"></font-awesome-icon>
					{{ $t('add_company_ad') }}
				</div>
			</div>
		</BaseHeader>
		<div class="main" v-if="!isLoading">
			<div v-if="ads">
				<div class="flex justify-between align-middle mb-4 mt-4">
					<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="self-center" />
					<div class="flex">
						<PaginationSizeSelect v-model="per_page" class="w-40" v-show="ads && ads.length" />
						<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" />
					</div>
				</div>
				<div class="flex flex-col">
					<div>
						<div class="align-middle inline-block min-w-full shadow overflow-hidden border-b border-gray-200">
							<div class="m-4" v-if="this.ads && this.ads.length <= 0">
								<b>{{ $t('no_entries_found') }}</b>
							</div>
							<table class="min-w-full" v-if="this.ads && this.ads.length > 0">
								<thead>
									<tr>
										<OverviewTableHeader :label="$t('validity').toString()" />
										<OverviewTableHeader :label="$t('title').toString()" />
										<OverviewTableHeader :label="$t('content').toString()" class="w-1/3" />
										<OverviewTableHeader :label="$t('filter').toString()" />
										<th class="px-6 py-3 border-b border-gray-200 bg-gray-50"></th>
									</tr>
								</thead>
								<tbody class="bg-white">
									<tr
										v-for="ad in this.ads"
										:key="ad.id"
										class="tr odd:bg-gray-200 rounded"
										@click="$router.push({ name: 'repair_shop.company_ads.ad_preview', params: { id: ad.id } })"
										:class="{ 'cursor-pointer': true }"
									>
										<td class="px-6 py-4">{{ formatDate(ad.start) }} - {{ formatDate(ad.end) }}</td>
										<td class="px-6 py-4">
											{{ ad.headline }}
										</td>
										<td class="px-6 py-4">
											{{ ad.content.length > 20 ? ad.content.substring(0, 20) : ad.content }}
										</td>
										<td class="px-6 py-4">
											<div v-if="ad.filters.length > 0">
												<div class="text-left" v-for="filter in formatSelectedFilters(ad.filters)" :key="filter.key">
													{{ filterNames.find(element => element.value === filter.key)?.display }}: {{ filter.value }}
												</div>
											</div>
											<div v-else>
												{{ $t('all') }}
											</div>
										</td>
										<td v-if="isAdAdmin" class="px-6 py-4 whitespace-no-wrap text-right text-lg font-medium flex justify-end">
											<div
												@click.stop="$router.push({ name: 'repair_shop.company_ads.create_ad', params: { id: ad.id } })"
												class="cursor-pointer px-4"
											>
												<font-awesome-icon :icon="['fas', 'edit']" />
											</div>

											<div class="cursor-pointer px-4" @click.stop="deleteOffer(ad.id)">
												<font-awesome-icon :icon="['fas', 'trash-alt']" :title="$t('remove').toString()" />
											</div>
										</td>
										<td v-else></td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>
			<div class="flex justify-between align-middle mb-4 mt-4 m-0">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="self-center" />
				<div class="flex">
					<PaginationSizeSelect v-model="per_page" class=" w-40" v-show="ads && ads.length" />
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" />
				</div>
			</div>
			<router-view />
		</div>
		<LoadingSpinner v-else />
	</div>
	<Forbidden v-else />
</template>

<script>
import { RepairshopsApiFactory } from '@/api/generated';
import BaseHeader from '@/components/navigation/BaseHeader.vue';
import Validate from '@/mixins/Validate';
import apiDefaults from '@/api/defaultConfiguration';
import PaginationNumberOfPages from '@/components/forms/buttons/PaginationNumberOfPages.vue';
import PaginationSizeSelect from '@/components/damagecases/forms/PaginationSizeSelect.vue';
import BasePagination from '@/components/forms/buttons/BasePagination.vue';
import OverviewTableHeader from '@/views/damagecases/OverviewTableHeader.vue';
import { getGermanDate } from '@/plugins/Utils';
import Authentication from '@/mixins/Authentication';
import { AdType } from '@/helpers/enums';
import Forbidden from '@/views/default/Forbidden.vue';
import SpecialOfferRepository from '@/api/repositories/SpecialOfferRepository';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';

export default {
	name: 'CompanyAds',
	computed: {
		AdType() {
			return AdType;
		},
	},
	components: {
		LoadingSpinner,
		Forbidden,
		OverviewTableHeader,
		BasePagination,
		PaginationSizeSelect,
		PaginationNumberOfPages,
		BaseHeader,
	},
	mixins: [Validate, Authentication],
	data() {
		return {
			variables: {},
			ads: null,
			current_page: 1,
			last_page: 1,
			total: 0,
			per_page: { display: '25', value: 25 },
			blockSearch: false,
			isLoading: false,
			filterNames: [],
		};
	},
	mounted() {
		if (this.isUserAllowedInLevel5or6) {
			this.isLoading = true;
			this.getFilters();
			this.getAllAds();
		}
	},
	methods: {
		clearAndCreate() {
			this.$store.commit('clearMarketingAds');
			this.$router.push({ name: 'repair_shop.company_ads.select_ad_type' });
		},
		formatSelectedFilters(filters) {
			const grouped = filters.reduce((acc, item) => {
				const key = item.special_offer_filter.key;
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(item);
				return acc;
			}, {});

			return Object.entries(grouped).map(([key, elements]) => ({
				key,
				value: elements.map(element => element.value).join(', '),
			}));
		},
		deleteOffer(id) {
			this.$swal({
				title: this.$t('sure').toString(),
				text: this.$t('remove_company_ad').toString(),
				icon: 'warning',
				cancelButtonText: this.$t('general.cancel'),
				showCancelButton: true,
			}).then(result => {
				if (result.value) {
					new SpecialOfferRepository()
						.deleteOffer(id)
						.then(response => {
							this.$swal(this.$t('general.success').toString(), this.$t('removed_company_ad').toString(), 'success');
							this.getAllAds();
						})
						.catch(error => {
							console.log(error);
							this.$swal(this.$t('general.error').toString(), this.$t('remove_company_ad_error').toString(), 'error');
						});
				}
			});
		},
		formatDate(timestamp) {
			return getGermanDate(timestamp);
		},
		getAllAds() {
			this.isLoading = true;
			new SpecialOfferRepository()
				.getOffers(this.current_page, this.per_page.value)
				.then(response => {
					this.last_page = response.data.meta.last_page;
					this.current_page = response.data.meta.current_page;
					this.total = response.data.meta.total;
					this.ads = response.data.data;
					this.isLoading = false;
				})
				.catch(error => {
					console.log(error);
					this.isLoading = false;
				});
		},
		getFilters() {
			new SpecialOfferRepository()
				.getFilters()
				.then(response => {
					const whiteLabels = ['vehicle_registration_first', 'vehicle_hu', 'vehicle_kilometrage', 'vehicle_kw', 'vehicle_displacement'];
					Object.values(response.data).forEach(values => {
						if (whiteLabels.includes(values[0].key)) {
							const name = values[0].label.split(' ')[0];
							this.filterNames.push({ display: name, value: values[0].key });
						} else {
							this.filterNames.push({ display: values[0].label, value: values[0].key });
						}
					});
				})
				.catch(e => {
					console.log(e);
				});
		},
	},
	watch: {
		// '$store.getters.getModalHistory': function(oldValue) {
		// 	if (oldValue == '') {
		// 		this.getAllAds();
		// 	}
		// },
		current_page: function(oldValue, newValue) {
			if (oldValue !== newValue) {
				this.getAllAds();
			}
		},
		per_page: function() {
			this.getAllAds();
		},
	},
};
</script>
